import { useState } from "react";
import BillingDetailsCard from "./components/BillingDetailsCard";
import Button from "components/Button";
import { transformBillingAddress } from "../tabs/billingDataTransformers";
import { BillingAddress } from "../types";
import useBillingInfo from "v2/hooks/billing/useBillingInfo";
import Iso3166CountryCodes from "misc/Iso3166CountryCodes";
import scraperApi, { ApiError } from "api";
import Toaster from "components/Toaster";
import LoadingCard from "../components/LoadingCard";
import BillingAddressForm from "./components/BillingAddressForm";
import { parseChargebeeFieldError } from "./parseChargebeeFieldError";
import { trackBillingAddressUpdated } from "utils/Tracking";


const FIELD_MAP: Record<string, keyof BillingAddress> = {
  first_name: "firstName",
  last_name: "lastName",
  company: "company",
  vat_number: "vatNumber",
  line1: "address",
  city: "city",
  state: "state",
  zip: "zip",
  country: "country",
};

export default function BillingAddressCard() {
  const { billingAddress, reload: refreshBillingInfo, fetcher } = useBillingInfo();
  const isLoading = !billingAddress.data && fetcher?.state === "loading";

  const transformedBillingAddress = transformBillingAddress(billingAddress?.data);

  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof BillingAddress, string>>>({});

  const clearFieldError = (fieldName: keyof BillingAddress) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const onSubmit = async (data: BillingAddress) => {
    setIsSubmitting(true);
    setFieldErrors({});

    try {
      const payload = {
        first_name: data.firstName,
        last_name: data.lastName,
        company: data.company,
        line1: data.address,
        city: data.city,
        state: data.state,
        zip: data.zip,
        country: data.country,
        vat_number: data.vatNumber,
      };

      await scraperApi.billing.setBillingAddress(payload);

      Toaster.success("Changes Saved", "Your billing address has been updated");

      trackBillingAddressUpdated();

      refreshBillingInfo(false);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save billing address:", error);

      const apiError = error as ApiError<any>;
      let handled = false;

      if (apiError?.error_code === "chargebee_error") {
        const fieldError = parseChargebeeFieldError(apiError.details?.message as string);
        const formFieldName = fieldError && FIELD_MAP[fieldError.field];
        if (formFieldName) {
          setFieldErrors((prev) => ({ ...prev, [formFieldName]: fieldError.message }));
          handled = true;
        }
      }

      if (!handled) {
        Toaster.error(
          "Couldn't save changes",
          apiError?.message || "Please check your details and try again.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // No address yet — show form expanded by default
  // Editing mode — show form with existing values
  if (!transformedBillingAddress || isEditing) {
    return (
      <LoadingCard isLoading={isLoading}>
        <BillingDetailsCard
          id="billing-address"
          title="Billing Address"
          subtitle="Update your billing information"
        >
          <BillingAddressForm
            defaultValues={isEditing ? transformedBillingAddress : undefined}
            fieldErrors={fieldErrors}
            onFieldChange={clearFieldError}
            onSubmit={onSubmit}
            onCancel={isEditing ? () => setIsEditing(false) : undefined}
            isSubmitting={isSubmitting}
          />
        </BillingDetailsCard>
      </LoadingCard>
    );
  }

  // Display mode — show current address
  const countryName = transformedBillingAddress?.country
    ? Object.values(Iso3166CountryCodes).find(
        (c) => c.countryCode === transformedBillingAddress.country,
      )?.name
    : "";

  return (
    <LoadingCard isLoading={isLoading}>
      <BillingDetailsCard
        id="billing-address"
        title="Billing Address"
        subtitle="Update your billing information"
      >
        <div className="flex flex-col gap-2">
          {transformedBillingAddress?.company && (
            <p className="text-sm font-medium text-gray-900">
              {transformedBillingAddress.company}
            </p>
          )}
          <p className="text-sm text-gray-900">
            {transformedBillingAddress?.firstName}{" "}
            {transformedBillingAddress?.lastName}
          </p>
          <p className="text-sm text-gray-600">
            {transformedBillingAddress?.address}
          </p>
          <p className="text-sm text-gray-600">
            {transformedBillingAddress?.city},{" "}
            {transformedBillingAddress?.state} {transformedBillingAddress?.zip}
          </p>
          {countryName && (
            <p className="text-sm text-gray-600">
              {countryName}
            </p>
          )}
          {transformedBillingAddress?.vatNumber && (
            <p className="text-sm text-gray-600">
              VAT: {transformedBillingAddress.vatNumber}
            </p>
          )}
        </div>

        <Button
          text="EDIT"
          onClick={() => setIsEditing(true)}
          className="button button-secondary w-fit"
          size="SM"
        />
      </BillingDetailsCard>
    </LoadingCard>
  );
}
